import * as React from "react"
import { DayPicker } from "react-day-picker"
import { ChevronLeft, ChevronRight } from "lucide-react"

import { cn } from "../../lib/utils"

// Design-system Calendar — wraps react-day-picker v9 with full token-driven
// styling. We intentionally do NOT import "react-day-picker/style.css" because
// the library's defaults (rounded radius, accent color, font sizes, focus
// rings) override our shadcn tokens and produce the off-system look users
// keep flagging. Every DayPicker slot below uses cn-* / token classes so
// the calendar matches Card / Button / Badge density throughout the app.
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("cn-calendar p-3", className)}
      classNames={{
        root: "rdp-root",
        months: "flex flex-col sm:flex-row gap-4",
        month: "flex flex-col gap-4 relative",
        month_caption: "flex justify-center pt-1 relative items-center h-7",
        caption_label: "text-sm font-medium text-foreground",
        nav: "absolute inset-x-1 top-1 flex items-center justify-between z-10 pointer-events-none",
        button_previous:
          "h-7 w-7 inline-flex items-center justify-center rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50 disabled:pointer-events-none pointer-events-auto",
        button_next:
          "h-7 w-7 inline-flex items-center justify-center rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50 disabled:pointer-events-none pointer-events-auto",
        month_grid: "w-full border-collapse",
        weekdays: "flex",
        weekday:
          "text-muted-foreground rounded-md w-9 font-normal text-[0.75rem] uppercase tracking-wider",
        week: "flex w-full mt-1",
        day: "relative p-0 text-center text-sm h-9 w-9",
        day_button:
          "h-9 w-9 inline-flex items-center justify-center rounded-md text-sm font-normal text-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none transition-colors disabled:opacity-30 disabled:pointer-events-none",
        selected:
          "[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground [&>button]:focus:bg-primary [&>button]:focus:text-primary-foreground",
        today: "[&>button]:bg-accent [&>button]:text-accent-foreground [&>button]:font-semibold",
        outside:
          "text-muted-foreground/60 [&>button]:text-muted-foreground/60",
        disabled: "text-muted-foreground opacity-40 [&>button]:opacity-40 [&>button]:pointer-events-none",
        range_start: "rounded-l-md [&>button]:bg-primary [&>button]:text-primary-foreground",
        range_end: "rounded-r-md [&>button]:bg-primary [&>button]:text-primary-foreground",
        range_middle:
          "rounded-none bg-accent [&>button]:bg-accent [&>button]:text-accent-foreground [&>button]:hover:bg-accent",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) => {
          const Icon = orientation === "left" ? ChevronLeft : ChevronRight
          return <Icon className="h-4 w-4 text-foreground" />
        },
      }}
      {...props}
    />
  )
}

Calendar.displayName = "Calendar"

export { Calendar }
